import offlineDB from "views/Dashboard/POSOffline/db/offlineDB";

const OFFLINE_QUEUE_KEY    = "webbie.offline.queue.v1";
const OFFLINE_CACHE_KEY    = "webbie.offline.cache.v1";
const OFFLINE_METRICS_KEY  = "webbie.offline.metrics.v1";
const MAX_QUEUE_ITEMS      = 1000;
const MAX_CACHE_ENTRIES    = 250;
const CACHE_TTL_MS         = 1000 * 60 * 60 * 24;  // 24 h
const SYNC_DEBOUNCE_MS     = 1000;
const SYNC_COOLDOWN_MS     = 10000;
const MUTATION_METHODS     = ["post", "put", "patch", "delete"];
const SENSITIVE_HEADERS    = ["authorization", "cookie", "set-cookie"];

let syncInProgress    = false;
let syncTimer;
let lastSyncStartedAt = 0;

const isBrowser = () => typeof window !== "undefined" && Boolean(window.localStorage);

const safeParse = (value, fallback) => {
  if (!value) return fallback;
  try { return JSON.parse(value); } catch { return fallback; }
};

const persist = (key, value) => {
  if (!isBrowser()) return false;
  try { window.localStorage.setItem(key, JSON.stringify(value)); return true; }
  catch { return false; }
};

const read = (key, fallback) => {
  if (!isBrowser()) return fallback;
  try { return safeParse(window.localStorage.getItem(key), fallback); }
  catch { return fallback; }
};

const remove = key => {
  if (!isBrowser()) return false;
  try { window.localStorage.removeItem(key); return true; }
  catch { return false; }
};

const removeCacheEntry = cacheKey => {
  const cache = read(OFFLINE_CACHE_KEY, {});
  delete cache[cacheKey];
  persist(OFFLINE_CACHE_KEY, cache);
};

const normaliseMethod = method => (method || "get").toLowerCase();

// Cached reads must not leak across accounts on a shared browser: if Merchant
// B logs in and goes offline before any read repopulates the cache, a flat
// url-keyed cache would hand back Merchant A's last cached response for the
// same endpoint. Prefixing every key with a fingerprint of the current
// access-token means a new login can never match a previous session's
// entries, independent of whether logout ran clearOfflineData() first.
const hashToken = token => {
  let hash = 0;
  for (let i = 0; i < token.length; i++) {
    hash = (hash * 31 + token.charCodeAt(i)) | 0;
  }
  return hash.toString(36);
};

const getSessionScope = () => {
  if (!isBrowser()) return "anon";
  const token = window.localStorage.getItem("access-token");
  return token ? hashToken(token) : "anon";
};

const buildCacheKey = config => {
  const method = normaliseMethod(config.method);
  const url    = config.url || "";
  const params = config.params ? JSON.stringify(config.params) : "";
  const scope  = config.offlineScope || "default";
  return `${getSessionScope()}:${scope}:${method}:${url}:${params}`;
};

const buildOperationId = config => {
  const method = normaliseMethod(config.method);
  const url    = config.url || "";
  const body   = typeof config.data === "string" ? config.data : JSON.stringify(config.data || {});
  return `${Date.now()}-${method}-${url}-${body}`;
};

const buildRequestId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const sanitiseHeaders = headers => {
  const safe = { ...(headers || {}) };
  Object.keys(safe).forEach(key => {
    if (SENSITIVE_HEADERS.includes(key.toLowerCase())) delete safe[key];
  });
  delete safe.common; delete safe.get; delete safe.post;
  delete safe.put; delete safe.patch; delete safe.delete;
  return safe;
};

const shouldCacheRead   = config => Boolean(config?.offlineCache === true);
const shouldQueueMutation = config => Boolean(config?.offlineQueue === true);

// ─── public exports ────────────────────────────────────────────────────────

export const createClientRequestId = buildRequestId;

export const clearOfflineData = () => {
  remove(OFFLINE_QUEUE_KEY);
  remove(OFFLINE_CACHE_KEY);
  remove(OFFLINE_METRICS_KEY);
  // Only the catalog cache (categories/products/cart/settings) is wiped here —
  // it's disposable and re-syncs on next login, so this keeps a previous
  // account's products/prices from showing on a shared device.
  //
  // offlineOrders/syncQueue are deliberately NOT touched. This function used
  // to delete the whole POSOfflineDB (indexedDB.deleteDatabase), which ran on
  // every logout AND on every forced session-expiry redirect (401/403,
  // failed auth check) — silently destroying any offline sale still waiting
  // to sync, with no warning. Those two tables are now scoped by account_id
  // (see offlineDB.js) instead: a returning account's pending orders survive
  // and resume syncing on next login, while a different account on the same
  // device simply won't see or sync entries that aren't theirs.
  offlineDB.categories.clear().catch(() => {});
  offlineDB.products.clear().catch(() => {});
  offlineDB.cart.clear().catch(() => {});
  offlineDB.settings.clear().catch(() => {});
};

export const getOfflineQueue  = ()      => read(OFFLINE_QUEUE_KEY, []);
export const setOfflineQueue  = queue => {
  const bounded = queue.slice(-MAX_QUEUE_ITEMS);
  persist(OFFLINE_QUEUE_KEY, bounded);
  return bounded;
};

const hasQueuedOperations = () => getOfflineQueue().length > 0;

export const getOfflineMetrics = () =>
  read(OFFLINE_METRICS_KEY, {
    attempted: 0, synced: 0, failed: 0, errorRate: 0, lastSyncAt: null,
  });

const setOfflineMetrics = m => persist(OFFLINE_METRICS_KEY, m);

export const recordSyncMetrics = ({ attempted = 0, synced = 0, failed = 0 }) => {
  if (!attempted) return getOfflineMetrics();
  const cur  = getOfflineMetrics();
  const next = {
    attempted: cur.attempted + attempted,
    synced:    cur.synced    + synced,
    failed:    cur.failed    + failed,
    lastSyncAt: new Date().toISOString(),
  };
  next.errorRate = next.attempted
    ? Number((next.failed / next.attempted).toFixed(4))
    : 0;
  setOfflineMetrics(next);
  return next;
};

export const shouldUseOfflineQueue = (config, error) => {
  const method          = normaliseMethod(config?.method);
  const isMutation      = MUTATION_METHODS.includes(method);
  const isReplay        = Boolean(config?.__offlineReplay);
  const hasServerResponse = Boolean(error?.response);
  return (
    isBrowser() &&
    shouldQueueMutation(config) &&
    isMutation &&
    !isReplay &&
    !hasServerResponse
  );
};

export const createOfflineOperation = config => {
  const operationId = config.headers?.["X-Client-Request-Id"] || buildOperationId(config);
  return {
    id:         operationId,
    method:     normaliseMethod(config.method),
    url:        config.url,
    baseURL:    config.baseURL,
    params:     config.params  || null,
    data:       config.data    || null,
    headers:    sanitiseHeaders(config.headers),
    retryCount: 0,
    status:     "pending",
    createdAt:  new Date().toISOString(),
    lastError:  null,
    conflictData: null,
  };
};

export const enqueueOfflineOperation = config => {
  const queue     = getOfflineQueue();
  const operation = createOfflineOperation(config);
  const already   = queue.some(item => item.id === operation.id);
  const nextQueue = already ? queue : setOfflineQueue([...queue, operation]);
  return { operation, queueDepth: nextQueue.length };
};

export const cacheReadResponse = (config, data) => {
  if (!isBrowser() || normaliseMethod(config.method) !== "get" || !shouldCacheRead(config)) return;
  // Deferred to a macrotask: this reads/sorts/writes the whole cache blob on
  // every cached GET response, and running it inline in the response
  // interceptor would delay the promise resolving back to the caller (and
  // thus the component's loading state) by that localStorage round-trip.
  const cacheKey = buildCacheKey(config);
  const expiresAt = Date.now() + (config.offlineCacheTtlMs || CACHE_TTL_MS);
  setTimeout(() => {
    const cache   = read(OFFLINE_CACHE_KEY, {});
    const entries = Object.entries({
      ...cache,
      [cacheKey]: { data, savedAt: Date.now(), expiresAt },
    })
      .sort(([, a], [, b]) => b.savedAt - a.savedAt)
      .slice(0, MAX_CACHE_ENTRIES);
    persist(OFFLINE_CACHE_KEY, Object.fromEntries(entries));
  }, 0);
};

export const getCachedReadResponse = config => {
  if (!isBrowser() || normaliseMethod(config?.method) !== "get" || !shouldCacheRead(config)) return null;
  const cacheKey = buildCacheKey(config);
  const cached   = read(OFFLINE_CACHE_KEY, {})[cacheKey];
  if (!cached) return null;
  if (cached.expiresAt <= Date.now()) { removeCacheEntry(cacheKey); return null; }
  return cached;
};

export const buildOfflineQueuedResponse = (config, operation, queueDepth) => ({
  data: {
    offlineQueued: true,
    operationId:  operation.id,
    queueDepth,
    message: "Saved locally. Webbie will sync this change when internet connectivity is restored.",
  },
  status:     202,
  statusText: "Accepted Offline",
  headers:    {},
  config,
  offline:    true,
});

export const buildCachedResponse = (config, cached) => ({
  data:       cached.data,
  status:     200,
  statusText: "OK Cached",
  headers:    { "x-webbie-cache": "offline" },
  config,
  offline:    true,
  cachedAt:   cached.savedAt,
});

// ─── sync ──────────────────────────────────────────────────────────────────

export const syncOfflineQueue = async client => {
  if (!isBrowser() || !navigator.onLine || syncInProgress || !hasQueuedOperations()) {
    return getOfflineMetrics();
  }

  syncInProgress    = true;
  lastSyncStartedAt = Date.now();

  const queue     = getOfflineQueue();
  let   synced    = 0;
  const remaining = [];   // items that failed or conflicted

  try {
    for (const item of queue) {
      try {
        const res = await client({
          method:   item.method,
          url:      item.url,
          baseURL:  item.baseURL,
          params:   item.params,
          data:     item.data,
          headers: {
            ...item.headers,
            "Idempotency-Key":  item.id,
            "X-Offline-Replay": "true",
          },
          __offlineReplay: true,
          offlineQueue:    false,
          offlineCache:    false,
        });

        // 409 Conflict — park the item so the merchant can resolve it
        if (res && res.status === 409) {
          remaining.push({
            ...item,
            status:       "conflict",
            conflictData: res.data || null,
            lastError:    res.data?.reason || "Conflict",
          });
          continue;
        }

        synced += 1;
      } catch (error) {
        remaining.push({
          ...item,
          retryCount: item.retryCount + 1,
          status:     "retrying",
          lastError:  error?.message || "Sync failed",
        });
      }
    }

    setOfflineQueue(remaining);
    return recordSyncMetrics({
      attempted: queue.length,
      synced,
      failed: remaining.filter(i => i.status === "retrying").length,
    });
  } finally {
    syncInProgress = false;
  }
};

export const scheduleOfflineSync = client => {
  if (!isBrowser() || !navigator.onLine || syncInProgress || !hasQueuedOperations()) return;
  const elapsed = Date.now() - lastSyncStartedAt;
  if (elapsed < SYNC_COOLDOWN_MS) return;
  window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(() => syncOfflineQueue(client), SYNC_DEBOUNCE_MS);
};

export const registerConnectivitySync = client => {
  if (!isBrowser()) return undefined;
  const runSync          = () => scheduleOfflineSync(client);
  const runSyncOnVisible = () => { if (document.visibilityState === "visible") runSync(); };

  window.addEventListener("online",            runSync);
  window.addEventListener("focus",             runSync);
  document.addEventListener("visibilitychange", runSyncOnVisible);
  runSync();

  return () => {
    window.clearTimeout(syncTimer);
    window.removeEventListener("online",            runSync);
    window.removeEventListener("focus",             runSync);
    document.removeEventListener("visibilitychange", runSyncOnVisible);
  };
};
